const isObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const asString = (value) => (typeof value === "string" ? value : value == null ? "" : String(value));
const asIdentifier = (value) => asString(value).trim();

const unwrapRecord = (value) => {
  let current = value;

  for (let depth = 0; depth < 3; depth += 1) {
    if (
      !isObject(current) ||
      !isObject(current.data) ||
      current.id ||
      current.elementId ||
      current.environmentIDs ||
      current.elementIDs ||
      current.labelData
    ) {
      break;
    }

    current = current.data;
  }

  return current;
};

const collectionEntries = (collection) => {
  if (Array.isArray(collection)) {
    return collection.map((value) => ["", value]);
  }

  if (isObject(collection)) {
    return Object.entries(collection);
  }

  return [];
};

const appendUniqueIdentifier = (values, seen, value) => {
  const identifier = asIdentifier(value);
  if (!identifier || seen.has(identifier)) {
    return;
  }

  seen.add(identifier);
  values.push(identifier);
};

const normalizeSourceSubElementLabels = (elementId, subElementLabels, errors) => {
  if (subElementLabels == null) {
    return [];
  }

  if (!Array.isArray(subElementLabels)) {
    errors.push(`Element ${elementId} has invalid sub-element labels.`);
    return [];
  }

  const seenKeys = new Set();

  return subElementLabels.reduce((normalized, subElementLabel, index) => {
    const key = asIdentifier(subElementLabel?.key);
    const displayName = asIdentifier(subElementLabel?.displayName);

    if (!key) {
      errors.push(`Element ${elementId} has a sub-element without a key at position ${index + 1}.`);
      return normalized;
    }

    if (seenKeys.has(key)) {
      errors.push(`Element ${elementId} contains duplicate sub-element key ${key}.`);
      return normalized;
    }

    seenKeys.add(key);

    if (!displayName) {
      errors.push(`Sub-element ${key} on element ${elementId} has no display name.`);
      return normalized;
    }

    normalized.push({
      key,
      displayName: asString(subElementLabel.displayName),
      description: asString(subElementLabel.description),
    });
    return normalized;
  }, []);
};

const normalizeSourceElementLabel = (record, fallbackId, errors) => {
  const element = unwrapRecord(record);
  const elementId = asIdentifier(element?.id || element?.elementId || fallbackId);

  if (!elementId) {
    errors.push("The System contains an element without an ID.");
    return null;
  }

  if (!isObject(element?.labelData)) {
    errors.push(`Element ${elementId} did not include complete label data.`);
    return null;
  }

  if (!asIdentifier(element.labelData.displayName)) {
    errors.push(`Element ${elementId} has no display name.`);
    return null;
  }

  return {
    elementId,
    displayName: asString(element.labelData.displayName),
    description: asString(element.labelData.description),
    subElementLabels: normalizeSourceSubElementLabels(
      elementId,
      element.labelData.subElementLabels,
      errors
    ),
  };
};

const normalizeGuideSubElementLabel = (subElementLabel = {}) => ({
  key: asString(subElementLabel.key),
  displayName: asString(subElementLabel.displayName),
  description: asString(subElementLabel.description),
});

const normalizeGuideElementLabel = (elementLabel = {}) => ({
  elementId: asString(elementLabel.elementId),
  displayName: asString(elementLabel.displayName),
  description: asString(elementLabel.description),
  subElementLabels: Array.isArray(elementLabel.subElementLabels)
    ? elementLabel.subElementLabels.map(normalizeGuideSubElementLabel)
    : [],
});

export const cloneGuideElementLabels = (elementLabels) =>
  Array.isArray(elementLabels) ? elementLabels.map(normalizeGuideElementLabel) : [];

export const extractSystemElementLabels = (systemPayload) => {
  const system = unwrapRecord(systemPayload);
  const errors = [];

  if (!isObject(system)) {
    return {
      elementLabels: [],
      environmentLabels: [],
      actionButtonLabels: [],
      errors: ["The selected System could not be loaded."],
    };
  }

  const authoredElementIds = [];
  const authoredElementIdSet = new Set();
  const discoveredElementIds = [];
  const discoveredElementIdSet = new Set();
  const elementRecordsById = new Map();

  const rememberElementRecord = (elementRecord, fallbackId) => {
    const element = unwrapRecord(elementRecord);
    const elementId = asIdentifier(element?.id || element?.elementId || fallbackId);

    if (!elementId) {
      errors.push("The System contains an element record without an ID.");
      return;
    }

    appendUniqueIdentifier(discoveredElementIds, discoveredElementIdSet, elementId);

    const existingRecord = elementRecordsById.get(elementId);
    if (!existingRecord || (!existingRecord.labelData && element?.labelData)) {
      elementRecordsById.set(elementId, element);
    }
  };

  const rememberElementCollection = (collection) => {
    collectionEntries(collection).forEach(([fallbackId, elementRecord]) => {
      rememberElementRecord(elementRecord, fallbackId);
    });
  };

  const rememberFirstAvailableElementCollection = (...collections) => {
    const collection = collections.find((candidate) => collectionEntries(candidate).length > 0);
    if (collection != null) {
      rememberElementCollection(collection);
    }
  };

  (Array.isArray(system.elementIDs) ? system.elementIDs : []).forEach((elementId) => {
    appendUniqueIdentifier(authoredElementIds, authoredElementIdSet, elementId);
  });

  rememberFirstAvailableElementCollection(
    system.elements,
    system.elementJsons,
    system.elementDatas
  );

  const environmentsById = new Map();
  const environmentIds = [];
  const environmentIdSet = new Set();

  const rememberEnvironment = (
    environmentRecord,
    fallbackId,
    { ignoreMissingId = false } = {}
  ) => {
    if (typeof environmentRecord === "string") {
      appendUniqueIdentifier(environmentIds, environmentIdSet, environmentRecord);
      return;
    }

    const environment = unwrapRecord(environmentRecord);
    const environmentId = asIdentifier(
      environment?.id || environment?.environmentId || environment?.environmentID || fallbackId
    );

    if (!environmentId) {
      if (ignoreMissingId) {
        return;
      }

      errors.push("The System contains an environment record without an ID.");
      return;
    }

    appendUniqueIdentifier(environmentIds, environmentIdSet, environmentId);
    environmentsById.set(environmentId, environment);
  };

  (Array.isArray(system.environmentIDs) ? system.environmentIDs : []).forEach((environmentId) => {
    appendUniqueIdentifier(environmentIds, environmentIdSet, environmentId);
  });

  collectionEntries(system.environments).forEach(([fallbackId, environmentRecord]) => {
    rememberEnvironment(environmentRecord, fallbackId);
  });

  const hasAuthoritativeEnvironmentReferences = environmentIds.length > 0;
  collectionEntries(system.environmentDatas).forEach(([fallbackId, environmentRecord]) => {
    rememberEnvironment(environmentRecord, fallbackId, {
      ignoreMissingId: hasAuthoritativeEnvironmentReferences,
    });
  });

  environmentIds.forEach((environmentId) => {
    const environment = environmentsById.get(environmentId);

    if (!environment) {
      errors.push(`Environment ${environmentId} did not include complete System data.`);
      return;
    }

    (Array.isArray(environment.elementIDs) ? environment.elementIDs : []).forEach((elementId) => {
      appendUniqueIdentifier(authoredElementIds, authoredElementIdSet, elementId);
    });

    rememberFirstAvailableElementCollection(
      environment.elementJsons,
      environment.elementDatas,
      environment.elements
    );
  });

  const environmentLabels = environmentIds.reduce((labels, environmentId) => {
    const environment = environmentsById.get(environmentId);
    if (!environment) {
      return labels;
    }

    const displayName = asIdentifier(environment.environmentName || environment.name);
    if (!displayName) {
      errors.push(`Environment ${environmentId} has no display name.`);
      return labels;
    }

    labels.push({
      environmentId,
      displayName: asString(environment.environmentName || environment.name),
      description: asString(environment.description),
    });
    return labels;
  }, []);

  const actionButtonLabels = [];
  const seenActionButtonIds = new Set();
  collectionEntries(
    system.environmentActionButtonDatas || system.actionButtonDatas
  ).forEach(([fallbackId, actionButtonRecord], index) => {
    const actionButton = unwrapRecord(actionButtonRecord);
    const actionButtonId = asIdentifier(actionButton?.id || actionButton?.actionButtonId || fallbackId);
    const displayName = asIdentifier(actionButton?.name || actionButton?.displayName);

    if (!actionButtonId) {
      errors.push(`The System contains an action button without an ID at position ${index + 1}.`);
      return;
    }
    if (seenActionButtonIds.has(actionButtonId)) {
      errors.push(`The System contains duplicate action button ID ${actionButtonId}.`);
      return;
    }
    seenActionButtonIds.add(actionButtonId);

    if (!displayName) {
      errors.push(`Action button ${actionButtonId} has no display name.`);
      return;
    }

    actionButtonLabels.push({
      actionButtonId,
      environmentId: asString(actionButton.environmentId),
      displayName: asString(actionButton.name || actionButton.displayName),
      deactivatedDisplayName: asString(
        actionButton.deactivatedName || actionButton.deactivatedDisplayName
      ),
      description: asString(actionButton.description),
    });
  });

  const orderedElementIds = [...authoredElementIds];
  const orderedElementIdSet = new Set(orderedElementIds);
  discoveredElementIds.forEach((elementId) => {
    appendUniqueIdentifier(orderedElementIds, orderedElementIdSet, elementId);
  });

  const elementLabels = orderedElementIds.reduce((labels, elementId) => {
    const record = elementRecordsById.get(elementId);
    if (!record) {
      errors.push(`Element ${elementId} did not include complete System data.`);
      return labels;
    }

    const label = normalizeSourceElementLabel(record, elementId, errors);
    if (label) {
      labels.push(label);
    }
    return labels;
  }, []);

  return { elementLabels, environmentLabels, actionButtonLabels, errors };
};

export const extractSystemGuideLabels = extractSystemElementLabels;

export const validateGuideElementLabels = (elementLabels) => {
  if (!Array.isArray(elementLabels)) {
    return ["Guide element labels must be an array."];
  }

  const errors = [];
  const seenElementIds = new Set();

  elementLabels.forEach((elementLabel, elementIndex) => {
    const elementId = asIdentifier(elementLabel?.elementId);
    const displayName = asIdentifier(elementLabel?.displayName);

    if (!elementId) {
      errors.push(`Guide element label ${elementIndex + 1} has no element ID.`);
    } else if (seenElementIds.has(elementId)) {
      errors.push(`Guide element labels contain duplicate element ID ${elementId}.`);
    } else {
      seenElementIds.add(elementId);
    }

    if (!displayName) {
      errors.push(`Guide element ${elementId || elementIndex + 1} has no display name.`);
    }

    if (!Array.isArray(elementLabel?.subElementLabels)) {
      errors.push(`Guide element ${elementId || elementIndex + 1} has invalid sub-element labels.`);
      return;
    }

    const seenKeys = new Set();
    elementLabel.subElementLabels.forEach((subElementLabel, subElementIndex) => {
      const key = asIdentifier(subElementLabel?.key);

      if (!key) {
        errors.push(
          `Guide element ${elementId || elementIndex + 1} has a sub-element without a key at position ${
            subElementIndex + 1
          }.`
        );
      } else if (seenKeys.has(key)) {
        errors.push(`Guide element ${elementId || elementIndex + 1} contains duplicate key ${key}.`);
      } else {
        seenKeys.add(key);
      }

      if (!asIdentifier(subElementLabel?.displayName)) {
        errors.push(
          `Guide sub-element ${key || subElementIndex + 1} on ${
            elementId || elementIndex + 1
          } has no display name.`
        );
      }
    });
  });

  return errors;
};

const countOrphans = (guideElementLabels, originalElementLabels) => {
  const originalById = new Map(originalElementLabels.map((label) => [label.elementId, label]));
  let orphanElementCount = 0;
  let orphanSubElementCount = 0;

  guideElementLabels.forEach((guideLabel) => {
    const originalLabel = originalById.get(guideLabel.elementId);
    if (!originalLabel) {
      orphanElementCount += 1;
      return;
    }

    const originalKeys = new Set(originalLabel.subElementLabels.map((label) => label.key));
    guideLabel.subElementLabels.forEach((subElementLabel) => {
      if (!originalKeys.has(subElementLabel.key)) {
        orphanSubElementCount += 1;
      }
    });
  });

  return { orphanElementCount, orphanSubElementCount };
};

export const reconcileGuideElementLabels = (guideElementLabels, originalElementLabels) => {
  const guideLabels = cloneGuideElementLabels(guideElementLabels);
  const originals = cloneGuideElementLabels(originalElementLabels);
  const validationErrors = validateGuideElementLabels(guideLabels);

  if (validationErrors.length > 0) {
    return {
      elementLabels: guideLabels,
      addedElementCount: 0,
      addedSubElementCount: 0,
      changed: false,
      validationErrors,
      ...countOrphans(guideLabels, originals),
    };
  }

  const guideById = new Map(guideLabels.map((label) => [label.elementId, label]));
  let addedElementCount = 0;
  let addedSubElementCount = 0;

  originals.forEach((originalLabel) => {
    const existingLabel = guideById.get(originalLabel.elementId);
    if (!existingLabel) {
      const appendedLabel = normalizeGuideElementLabel(originalLabel);
      guideLabels.push(appendedLabel);
      guideById.set(appendedLabel.elementId, appendedLabel);
      addedElementCount += 1;
      return;
    }

    const existingKeys = new Set(existingLabel.subElementLabels.map((label) => label.key));
    originalLabel.subElementLabels.forEach((originalSubElementLabel) => {
      if (!existingKeys.has(originalSubElementLabel.key)) {
        existingLabel.subElementLabels.push(
          normalizeGuideSubElementLabel(originalSubElementLabel)
        );
        existingKeys.add(originalSubElementLabel.key);
        addedSubElementCount += 1;
      }
    });
  });

  return {
    elementLabels: guideLabels,
    addedElementCount,
    addedSubElementCount,
    changed: addedElementCount > 0 || addedSubElementCount > 0,
    validationErrors: [],
    ...countOrphans(guideLabels, originals),
  };
};

export const elementLabelDiffersFromOriginal = (guideLabel, originalLabel) => {
  if (!guideLabel || !originalLabel) {
    return false;
  }

  if (
    guideLabel.displayName !== originalLabel.displayName ||
    guideLabel.description !== originalLabel.description
  ) {
    return true;
  }

  const guideSubElementsByKey = new Map(
    guideLabel.subElementLabels.map((label) => [label.key, label])
  );

  return originalLabel.subElementLabels.some((originalSubElementLabel) => {
    const guideSubElementLabel = guideSubElementsByKey.get(originalSubElementLabel.key);
    return (
      !guideSubElementLabel ||
      guideSubElementLabel.displayName !== originalSubElementLabel.displayName ||
      guideSubElementLabel.description !== originalSubElementLabel.description
    );
  });
};

export const getElementLabelStats = (guideElementLabels, originalElementLabels) => {
  const guideLabels = cloneGuideElementLabels(guideElementLabels);
  const originals = cloneGuideElementLabels(originalElementLabels);
  const guideById = new Map(guideLabels.map((label) => [label.elementId, label]));
  const differingElementCount = originals.reduce(
    (count, originalLabel) =>
      count +
      (elementLabelDiffersFromOriginal(guideById.get(originalLabel.elementId), originalLabel)
        ? 1
        : 0),
    0
  );

  return {
    currentElementCount: originals.length,
    differingElementCount,
    ...countOrphans(guideLabels, originals),
  };
};

export const updateElementLabelField = (
  guideElementLabels,
  elementId,
  field,
  value,
  subElementKey = null
) =>
  cloneGuideElementLabels(guideElementLabels).map((elementLabel) => {
    if (elementLabel.elementId !== elementId) {
      return elementLabel;
    }

    if (subElementKey == null) {
      return { ...elementLabel, [field]: asString(value) };
    }

    return {
      ...elementLabel,
      subElementLabels: elementLabel.subElementLabels.map((subElementLabel) =>
        subElementLabel.key === subElementKey
          ? { ...subElementLabel, [field]: asString(value) }
          : subElementLabel
      ),
    };
  });

export const resetElementLabelField = (
  guideElementLabels,
  originalElementLabels,
  elementId,
  field,
  subElementKey = null
) => {
  const originalElementLabel = originalElementLabels.find(
    (elementLabel) => elementLabel.elementId === elementId
  );
  if (!originalElementLabel) {
    return cloneGuideElementLabels(guideElementLabels);
  }

  if (subElementKey == null) {
    return updateElementLabelField(
      guideElementLabels,
      elementId,
      field,
      originalElementLabel[field]
    );
  }

  const originalSubElementLabel = originalElementLabel.subElementLabels.find(
    (subElementLabel) => subElementLabel.key === subElementKey
  );
  if (!originalSubElementLabel) {
    return cloneGuideElementLabels(guideElementLabels);
  }

  return updateElementLabelField(
    guideElementLabels,
    elementId,
    field,
    originalSubElementLabel[field],
    subElementKey
  );
};

export const resetElementLabel = (guideElementLabels, originalElementLabels, elementId) => {
  const originalElementLabel = originalElementLabels.find(
    (elementLabel) => elementLabel.elementId === elementId
  );
  if (!originalElementLabel) {
    return cloneGuideElementLabels(guideElementLabels);
  }

  return cloneGuideElementLabels(guideElementLabels).map((guideElementLabel) => {
    if (guideElementLabel.elementId !== elementId) {
      return guideElementLabel;
    }

    const originalSubElementsByKey = new Map(
      originalElementLabel.subElementLabels.map((label) => [label.key, label])
    );
    const resetSubElementLabels = guideElementLabel.subElementLabels.map((subElementLabel) =>
      originalSubElementsByKey.has(subElementLabel.key)
        ? normalizeGuideSubElementLabel(originalSubElementsByKey.get(subElementLabel.key))
        : subElementLabel
    );
    const resetKeys = new Set(resetSubElementLabels.map((label) => label.key));

    originalElementLabel.subElementLabels.forEach((subElementLabel) => {
      if (!resetKeys.has(subElementLabel.key)) {
        resetSubElementLabels.push(normalizeGuideSubElementLabel(subElementLabel));
      }
    });

    return {
      ...guideElementLabel,
      displayName: originalElementLabel.displayName,
      description: originalElementLabel.description,
      subElementLabels: resetSubElementLabels,
    };
  });
};

export const resetAllCurrentElementLabels = (guideElementLabels, originalElementLabels) => {
  const reconciliation = reconcileGuideElementLabels(guideElementLabels, originalElementLabels);

  return originalElementLabels.reduce(
    (labels, originalElementLabel) =>
      resetElementLabel(labels, originalElementLabels, originalElementLabel.elementId),
    reconciliation.elementLabels
  );
};

const normalizeGuideEnvironmentLabel = (label = {}) => ({
  environmentId: asString(label.environmentId),
  displayName: asString(label.displayName),
  description: asString(label.description),
});

const normalizeGuideActionButtonLabel = (label = {}) => ({
  actionButtonId: asString(label.actionButtonId),
  environmentId: asString(label.environmentId),
  displayName: asString(label.displayName),
  deactivatedDisplayName: asString(label.deactivatedDisplayName),
  description: asString(label.description),
});

export const cloneGuideEnvironmentLabels = (labels) =>
  Array.isArray(labels) ? labels.map(normalizeGuideEnvironmentLabel) : [];

export const cloneGuideActionButtonLabels = (labels) =>
  Array.isArray(labels) ? labels.map(normalizeGuideActionButtonLabel) : [];

export const updateRecoveryDestinationsForEnvironmentLabels = (
  destinations,
  previousLabels = [],
  nextLabels = []
) => {
  if (!Array.isArray(destinations)) return destinations;

  const previousNames = new Map(
    previousLabels.map((label) => [label.environmentId, asIdentifier(label.displayName)])
  );
  const nextNames = new Map(
    nextLabels.map((label) => [label.environmentId, asIdentifier(label.displayName)])
  );

  return destinations.map((destination) => {
    const name = nextNames.get(destination?.environmentId);
    if (!name || name === previousNames.get(destination.environmentId)) return destination;

    const text = `To continue, go to the '${name}' environment.`;
    if (text === destination.text) return destination;

    const updated = { ...destination, text };
    // The normal guide save regenerates or reuses audio for the changed text.
    delete updated.audioKey;
    delete updated.audioHash;
    delete updated.audioPresignedUrl;
    return updated;
  });
};

const validateFlatLabels = (labels, idField, typeName) => {
  if (!Array.isArray(labels)) {
    return [`Guide ${typeName} labels must be an array.`];
  }

  const errors = [];
  const seenIds = new Set();
  labels.forEach((label, index) => {
    const id = asIdentifier(label?.[idField]);
    if (!id) {
      errors.push(`Guide ${typeName} label ${index + 1} has no ${typeName} ID.`);
    } else if (seenIds.has(id)) {
      errors.push(`Guide ${typeName} labels contain duplicate ${typeName} ID ${id}.`);
    } else {
      seenIds.add(id);
    }

    if (!asIdentifier(label?.displayName)) {
      errors.push(`Guide ${typeName} ${id || index + 1} has no display name.`);
    }
  });
  return errors;
};

export const validateGuideEnvironmentLabels = (labels) =>
  validateFlatLabels(labels, "environmentId", "environment");

export const validateGuideActionButtonLabels = (labels) =>
  validateFlatLabels(labels, "actionButtonId", "action button");

const reconcileFlatLabels = (guideLabels, originalLabels, idField, cloneLabels) => {
  const current = cloneLabels(guideLabels);
  const originals = cloneLabels(originalLabels);
  const originalIds = new Set(originals.map((label) => label[idField]));
  const currentById = new Map(current.map((label) => [label[idField], label]));
  let addedCount = 0;

  originals.forEach((original) => {
    if (!currentById.has(original[idField])) {
      const appended = cloneLabels([original])[0];
      current.push(appended);
      currentById.set(appended[idField], appended);
      addedCount += 1;
    }
  });

  return {
    labels: current,
    addedCount,
    orphanCount: current.filter((label) => !originalIds.has(label[idField])).length,
    changed: addedCount > 0,
  };
};

export const reconcileGuideEnvironmentLabels = (guideLabels, originalLabels) => ({
  ...reconcileFlatLabels(
    guideLabels,
    originalLabels,
    "environmentId",
    cloneGuideEnvironmentLabels
  ),
  validationErrors: validateGuideEnvironmentLabels(cloneGuideEnvironmentLabels(guideLabels)),
});

export const reconcileGuideActionButtonLabels = (guideLabels, originalLabels) => ({
  ...reconcileFlatLabels(
    guideLabels,
    originalLabels,
    "actionButtonId",
    cloneGuideActionButtonLabels
  ),
  validationErrors: validateGuideActionButtonLabels(cloneGuideActionButtonLabels(guideLabels)),
});

export const flatLabelDiffersFromOriginal = (guideLabel, originalLabel, fields) =>
  Boolean(
    guideLabel &&
      originalLabel &&
      fields.some((field) => guideLabel[field] !== originalLabel[field])
  );

export const getFlatLabelStats = (guideLabels, originalLabels, idField, fields) => {
  const guideById = new Map(guideLabels.map((label) => [label[idField], label]));
  const originalIds = new Set(originalLabels.map((label) => label[idField]));
  return {
    currentCount: originalLabels.length,
    differingCount: originalLabels.filter((original) =>
      flatLabelDiffersFromOriginal(guideById.get(original[idField]), original, fields)
    ).length,
    orphanCount: guideLabels.filter((label) => !originalIds.has(label[idField])).length,
  };
};

export const updateFlatLabelField = (labels, idField, id, field, value) =>
  labels.map((label) =>
    label[idField] === id ? { ...label, [field]: asString(value) } : { ...label }
  );

export const resetFlatLabelField = (
  labels,
  originals,
  idField,
  id,
  field
) => {
  const original = originals.find((label) => label[idField] === id);
  return original
    ? updateFlatLabelField(labels, idField, id, field, original[field])
    : labels.map((label) => ({ ...label }));
};

export const resetFlatLabel = (labels, originals, idField, id, fields) => {
  const original = originals.find((label) => label[idField] === id);
  if (!original) return labels.map((label) => ({ ...label }));
  return labels.map((label) =>
    label[idField] === id
      ? fields.reduce((next, field) => ({ ...next, [field]: original[field] }), { ...label })
      : { ...label }
  );
};

export const resetAllFlatLabels = (labels, originals, idField, fields) =>
  originals.reduce(
    (current, original) =>
      resetFlatLabel(current, originals, idField, original[idField], fields),
    reconcileFlatLabels(labels, originals, idField, (value) =>
      value.map((label) => ({ ...label }))
    ).labels
  );
